export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;
  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiRequestInit {
  /** Extra headers merged on top of the default JSON content-type + accept. */
  headers?: Record<string, string>;
  /**
   * Skip the derived default-tenant x-tenant-context header — used by
   * routes that legitimately operate outside any tenant (master scope,
   * /auth/me, /setup/*).
   */
  skipTenantScope?: boolean;
  /** Override the default request timeout. */
  timeoutMs?: number;
  /** Caller-owned abort signal; replaces the timeout when supplied. */
  signal?: AbortSignal;
}

const AUTH_REFRESH_URL = '/api/v1/auth/refresh';
const AUTH_NO_AUTO_REFRESH_URLS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/logout',
  AUTH_REFRESH_URL,
  '/api/v1/auth/mfa/challenge/verify',
  '/api/v1/auth/mfa/setup/start',
  '/api/v1/auth/mfa/setup/verify',
  '/api/v1/auth/passkeys/login/start',
  '/api/v1/auth/passkeys/login/verify',
  '/api/v1/auth/passkeys/setup/start',
  '/api/v1/auth/passkeys/setup/complete',
]);

// Default-tenant scope getter is set once by TenantScopeProvider on mount.
// It is derived from install/user state only; commercial tenant pages pass
// x-tenant-context explicitly per request.
let tenantScopeGetter: () => string | null = () => null;
export function _setTenantScopeGetter(fn: () => string | null) {
  tenantScopeGetter = fn;
}

async function parseJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function apiErrorFromResponse(res: Response, json: unknown): ApiError {
  const env = (json as { error?: { message?: string; code?: string; details?: unknown } }).error;
  return new ApiError(
    env?.message ?? res.statusText,
    res.status,
    env?.code ?? 'unknown',
    env?.details,
  );
}

/**
 * How long any single request may hang before it becomes a `timeout` error.
 * Generous, because controller-backed reads are genuinely slow — but finite,
 * because an infinite spinner is not a state an operator can act on.
 */
const DEFAULT_TIMEOUT_MS = 20_000;

let refreshSessionPromise: Promise<void> | null = null;

async function refreshSession(): Promise<void> {
  if (!refreshSessionPromise) {
    refreshSessionPromise = (async () => {
      const res = await fetch(AUTH_REFRESH_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (res.status === 204 || res.ok) return;
      throw apiErrorFromResponse(res, await parseJson(res));
    })().finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

function canAutoRefresh(url: string): boolean {
  return !AUTH_NO_AUTO_REFRESH_URLS.has(url);
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  init?: ApiRequestInit,
  retryOnUnauthorized = true,
): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    accept: 'application/json',
    ...(init?.headers ?? {}),
  };
  if (!init?.skipTenantScope) {
    const scope = tenantScopeGetter();
    if (scope && !('x-tenant-context' in headers)) {
      headers['x-tenant-context'] = scope;
    }
  }
  // A request with no timeout can hang until the browser gives up, which can
  // be minutes. Observed live: /admin/hardware/inventory stayed `pending`
  // indefinitely while the controller was unreachable, so the Hardware page
  // showed a spinner that was never going to resolve — no error, no retry, no
  // way out. "Never leave a permanent spinner as the final state" is only
  // enforceable if a request is guaranteed to end.
  //
  // Times out as a `timeout` ApiError, which every page already renders like
  // any other failure.
  const timeoutMs = init?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: init?.signal ?? controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted) {
      throw new ApiError(
        `The server did not respond within ${Math.round(timeoutMs / 1000)} seconds. It may be waiting on the controller.`,
        0,
        'timeout',
      );
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 204) return undefined as unknown as T;
  const json = await parseJson(res);
  if (!res.ok) {
    const err = apiErrorFromResponse(res, json);
    if (res.status === 401 && retryOnUnauthorized && canAutoRefresh(url)) {
      try {
        await refreshSession();
        return request<T>(method, url, body, init, false);
      } catch {
        throw err;
      }
    }
    throw err;
  }
  return json as T;
}

export const apiClient = {
  get: <T>(url: string, init?: ApiRequestInit) => request<T>('GET', url, undefined, init),
  post: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
    request<T>('POST', url, body, init),
  patch: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
    request<T>('PATCH', url, body, init),
  put: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
    request<T>('PUT', url, body, init),
  delete: <T>(url: string, init?: ApiRequestInit) => request<T>('DELETE', url, undefined, init),

  /**
   * Fetch a binary resource (e.g. a snapshot image) authenticated via the
   * same session cookie + tenant-scope headers as every other request.
   * Throws ApiError on non-OK responses; returns the raw Blob on success.
   */
  async getBlob(url: string, init?: ApiRequestInit): Promise<Blob> {
    const headers: Record<string, string> = {
      ...(init?.headers ?? {}),
    };
    if (!init?.skipTenantScope) {
      const scope = tenantScopeGetter();
      if (scope && !('x-tenant-context' in headers)) {
        headers['x-tenant-context'] = scope;
      }
    }
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      const json = await parseJson(res);
      throw apiErrorFromResponse(res, json);
    }
    return res.blob();
  },

  /**
   * DELETE with a JSON body. Standard `apiClient.delete` sends no body —
   * some operator endpoints (e.g. `/admin/outbox/:id`) require a
   * `{ reason }` payload alongside the DELETE so the audit log has
   * non-empty context. Use this instead of writing one-off `fetch()`
   * wrappers in components.
   */
  deleteWithBody: <T>(url: string, body: unknown, init?: ApiRequestInit) =>
    request<T>('DELETE', url, body, init),

  /**
   * P1-12: Returns a bound client that injects `Idempotency-Key: <key>`
   * on every mutation. Use this when a user-submitted intent (creating
   * an employee, enrolling a credential, …) drives a controller
   * side-effect. Generate the key once when the modal opens and keep
   * passing the same key across retries within that intent — the
   * backend will collapse duplicate submissions to a single result.
   *
   * Example:
   *   const key = newIdempotencyKey();
   *   await apiClient.withIdempotencyKey(key).post('/api/v1/employees', body);
   */
  withIdempotencyKey(key: string) {
    const inject = (init?: ApiRequestInit): ApiRequestInit => ({
      ...init,
      headers: { ...(init?.headers ?? {}), 'idempotency-key': key },
    });
    return {
      get: <T>(url: string, init?: ApiRequestInit) =>
        request<T>('GET', url, undefined, inject(init)),
      post: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
        request<T>('POST', url, body, inject(init)),
      patch: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
        request<T>('PATCH', url, body, inject(init)),
      put: <T>(url: string, body?: unknown, init?: ApiRequestInit) =>
        request<T>('PUT', url, body, inject(init)),
      delete: <T>(url: string, init?: ApiRequestInit) =>
        request<T>('DELETE', url, undefined, inject(init)),
      deleteWithBody: <T>(url: string, body: unknown, init?: ApiRequestInit) =>
        request<T>('DELETE', url, body, inject(init)),
    };
  },
};

/**
 * P1-12: Generate a stable idempotency key for a user-submitted intent.
 * Prefers crypto.randomUUID() where available; falls back to a 128-bit
 * random hex string for older browsers. The caller is responsible for
 * holding the key for the lifetime of the intent (e.g., a modal).
 */
export function newIdempotencyKey(): string {
  const cryptoApi: Crypto | undefined =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Last-resort fallback. Never reached in real browsers.
  return `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
